import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Scale, Plus, LogOut, FileText, Clock } from 'lucide-react';
import { api } from '@/App';
import { toast } from 'sonner';

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCases();
  }, []);

  const loadCases = async () => {
    try {
      const response = await api.get('/cases');
      setCases(response.data);
    } catch (error) {
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      // Clear localStorage
      localStorage.removeItem('session_token');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Scale className="w-8 h-8 text-blue-700" />
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>LegalCheck</span>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full border-2 border-blue-200" />
              <span className="text-slate-700 font-medium hidden sm:block">{user.name}</span>
            </div>
            <Button 
              data-testid="logout-button"
              onClick={handleLogout}
              variant="outline"
              className="rounded-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Your Legal Cases
            </h1>
            <p className="text-slate-600">Review your submitted cases and their AI analysis results</p>
          </div>
          
          <Button 
            data-testid="submit-new-case-button"
            onClick={() => navigate('/submit')}
            className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <Plus className="w-5 h-5 mr-2" />
            Submit New Case
          </Button>
        </div>

        {/* Cases Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-12 h-12 border-4 border-blue-700 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : cases.length === 0 ? (
          <Card className="p-12 text-center bg-white border-2 border-dashed border-slate-300 rounded-2xl">
            <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-700 mb-2">No Cases Yet</h3>
            <p className="text-slate-500 mb-6">Submit your first case to get started with AI-powered legal analysis</p>
            <Button 
              data-testid="submit-first-case-button"
              onClick={() => navigate('/submit')}
              className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-3 rounded-full"
            >
              Submit Your First Case
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cases.map((caseItem) => (
              <Card 
                key={caseItem.id}
                data-testid={`case-card-${caseItem.id}`}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200 cursor-pointer transform hover:scale-105"
                onClick={() => navigate(`/case/${caseItem.id}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                    {caseItem.category}
                  </div>
                  <Clock className="w-4 h-4 text-slate-400" />
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 mb-3 line-clamp-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {caseItem.title}
                </h3>
                
                <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                  {caseItem.description}
                </p>
                
                <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                  <span className="text-xs text-slate-500">
                    {formatDate(caseItem.created_at)}
                  </span>
                  <Button 
                    data-testid={`view-case-${caseItem.id}`}
                    size="sm" 
                    className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-4"
                  >
                    View Analysis
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-12 p-6 bg-amber-50 border-2 border-amber-200 rounded-xl">
          <p className="text-sm text-amber-900 font-medium text-center">
            <strong>⚠️ Important:</strong> All analyses are for informational purposes only and do not constitute legal advice. 
            Consult a licensed attorney for professional guidance.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;